import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, Lock, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { refreshBrazilianStatus } from '../utils/detectBrazil';
import { createTranslator } from '../utils/detectLanguage';
import { authPromptModalPtBR } from './mappings/authPromptModal.pt-br';
import { authPromptModalES } from './mappings/authPromptModal.es';

const AuthPromptModal = ({ isOpen, onClose, feature = 'default', title = null }) => {
  const navigate = useNavigate();
  // Force refresh to ensure we have the latest status
  const isBrazilian = refreshBrazilianStatus();

  // Translation helper using the proper utility
  const t = createTranslator({
    'pt-br': authPromptModalPtBR,
    'es': authPromptModalES
  });

  const featureMessages = {
    default: {
      title: "Sign in to continue",
      description: "Create a free account to access this feature"
    },
    chat: {
      title: "Start chatting with AI",
      description: "Sign in to get personalized sports insights and predictions"
    },
    history: {
      title: "View your history",
      description: "Sign in to access your conversation history and saved predictions"
    },
    sportsFeed: {
      title: "Access live sports data",
      description: "Sign in to get real-time sports updates and analytics"
    },
    predictions: {
      title: "Get AI predictions",
      description: "Sign in to receive AI-powered game predictions and analysis"
    },
    starCoins: {
      title: "Manage Star Coins",
      description: "Sign in to view and use your Star Coins balance"
    }
  };

  // Use provided title or fall back to feature messages
  const content = title
    ? { title, description: t("Join thousands of users getting AI-powered sports insights") }
    : (featureMessages[feature] || featureMessages.default);

  const handleSignUp = () => {
    onClose();
    // Get landing page from sessionStorage (set when user first arrives at /pt-br or /arena)
    const landingPage = sessionStorage.getItem('landingPage');

    // Pass language state if Brazilian, so Login.js can auto-detect it
    navigate('/login', {
      state: {
        isSignUp: true,
        referrer: feature,
        language: isBrazilian ? 'BRL' : undefined,
        fromLandingPage: landingPage // '/pt-br', '/arena', or null
      }
    });
  };

  const handleLogin = () => {
    onClose();
    // Pass language state if Brazilian, so Login.js can auto-detect it
    navigate('/login', {
      state: {
        isSignUp: false,
        referrer: feature,
        language: isBrazilian ? 'BRL' : undefined
      }
    });
  };

  if (!isOpen) return null;

  // Premium terminal-style dark design with glass morphism
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop with subtle blur */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", duration: 0.3 }}
              className="w-full max-w-md"
            >
              {/* Glass card with border glow */}
              <div className="relative bg-black/80 backdrop-blur-xl border border-white/[0.08] rounded-lg overflow-hidden hover:border-white/[0.12] transition-all duration-200 shadow-2xl">
                {/* Live indicator */}
                <div className="absolute top-4 right-12">
                  <span className="w-1.5 h-1.5 bg-emerald-500/70 rounded-full animate-pulse" />
                </div>

                {/* Close button */}
                <button
                  onClick={onClose}
                  className="absolute right-4 top-4 p-1.5 rounded-lg hover:bg-white/[0.05] transition-colors z-10"
                >
                  <X className="w-4 h-4 text-white/40 hover:text-white/80" />
                </button>

                {/* Header section with gradient */}
                <div className="bg-gradient-to-r from-emerald-500/[0.02] to-violet-500/[0.02] border-b border-white/[0.05] px-6 py-5">
                  <h3 className="text-xs font-medium tracking-[0.2em] text-white/30 uppercase mb-3">
                    {t('AUTHENTICATION REQUIRED')}
                  </h3>
                  <h2 className="text-2xl text-white/95 mb-2 tracking-tight">
                    {t(content.title)}
                  </h2>
                  <p className="text-sm text-white/50 leading-relaxed">
                    {t(content.description)}
                  </p>
                </div>

                {/* Content section */}
                <div className="px-6 py-6">
                  {/* Primary CTA - Sign Up */}
                  <button
                    onClick={handleSignUp}
                    className="w-full relative px-8 py-3.5 rounded-md bg-gradient-to-r from-emerald-500/90 to-violet-500/90 hover:from-emerald-500 hover:to-violet-500 text-white text-sm font-medium tracking-wide transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20"
                  >
                    {t('Continue with Google')}
                    <ArrowRight className="w-4 h-4" />
                  </button>

                  {/* Divider */}
                  <div className="flex items-center gap-3 my-5">
                    <div className="flex-1 h-px bg-white/[0.05]" />
                    <span className="text-xs text-white/30 uppercase tracking-wider">{t('or')}</span>
                    <div className="flex-1 h-px bg-white/[0.05]" />
                  </div>

                  {/* Secondary CTA - Login */}
                  <button
                    onClick={handleLogin}
                    className="w-full py-3 px-4 rounded-md bg-white/[0.02] border border-white/[0.05] text-white/70 font-medium hover:bg-white/[0.05] hover:text-white/90 transition-all duration-200 text-sm tracking-wide"
                  >
                    {t('Already have an account? Sign in')}
                  </button>

                  {/* Secure payment message */}
                  <div className="flex justify-center items-center mt-5">
                    <Lock className="w-3 h-3 text-white/30 mr-1.5" />
                    <p className="text-[10px] text-white/30 uppercase tracking-wider">
                      {t('Secured by Google')}
                    </p>
                  </div>
                </div>

                {/* Trust indicators section */}
                <div className="border-t border-white/[0.05] px-6 py-4">
                  <h3 className="text-[10px] font-medium tracking-[0.2em] text-white/30 uppercase mb-3">
                    {t('No Risk, No Catches')}
                  </h3>
                  <ul className="space-y-2.5">
                    {[
                      t('100% FREE — forever'),
                      t('Zero credit card required'),
                      t('Upgrade only if you want more power')
                    ].map((benefit, idx) => (
                      <motion.li
                        key={idx}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: 0.1 + (idx * 0.05) }}
                        className="flex items-start group"
                      >
                        <div className="flex-shrink-0 w-4 h-4 mt-0.5 rounded bg-emerald-500/[0.15] flex items-center justify-center group-hover:bg-emerald-500/[0.25] transition-colors">
                          <Check size={10} className="text-emerald-400/80 group-hover:text-emerald-400 transition-colors" />
                        </div>
                        <span className="ml-3 text-sm text-white/70 group-hover:text-white/90 transition-colors">
                          {benefit}
                        </span>
                      </motion.li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AuthPromptModal;